import { RefObject, Suspense, useCallback, useEffect, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { useNavigate } from "react-router"
import { DotsThreeVertical, MagnifyingGlass } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { Drawer, DrawerBody, DrawerContent, DrawerFooter } from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { canUserTakeAppointmentWithService } from "@/lib/can-user-take-appointment-with-service"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { cn } from "@/lib/utils"
import { getChatRoute, getTakenChatRoute } from "@/modules/chats/lib/routing-helpers"
import { useChatRequestTakeOverStore } from "@/modules/chats/stores/useChatRequestTakeOverStore"
import { AppointmentDrawerCardHeader } from "@/modules/common/waiting-room/appointment/appointment-drawer-card-header"
import { AppointmentDrawerCardHeaderReviewStatus } from "@/modules/common/waiting-room/appointment/appointment-drawer-card-header-review-status"
import { AppointmentDrawerQuestionnaire } from "@/modules/common/waiting-room/appointment/appointment-drawer-questionnaire"
import { AppointmentDrawerStatusIndicator } from "@/modules/common/waiting-room/appointment/appointment-drawer-status-indicator"
import { AttachmentList } from "@/modules/patients/components/attachments/attachment-list"
import { CallLineBanner } from "@/modules/patients/components/call-line-banner"
import { GeneralHealthInformationSummary } from "@/modules/patients/detail/progress-note/components/general-health-information-summary"
import { RequestedMedicationsSummary } from "@/modules/patients/detail/progress-note/components/requested-medications-summary"
import { VitalSignsSummary } from "@/modules/patients/detail/progress-note/components/vital-signs-summary"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { usePatientAttachmentSubscription } from "@/modules/patients/services/usePatientAttachmentSubscription"
import { useRequestTakeOverPatientMutation } from "@/modules/patients/services/useRequestTakeOverPatientMutation"
import { appointmentDrawer_waitingRoomAppointment$key } from "@/modules/waiting-room/components/__generated__/appointmentDrawer_waitingRoomAppointment.graphql"
import { appointmentDrawerData_WaitingRoomAppointment$key } from "@/modules/waiting-room/components/__generated__/appointmentDrawerData_WaitingRoomAppointment.graphql"
import { appointmentDrawerPatient_WaitingRoomAppointment$key } from "@/modules/waiting-room/components/__generated__/appointmentDrawerPatient_WaitingRoomAppointment.graphql"
import { AppointmentActions } from "@/modules/waiting-room/components/appointment-drawer/appointment-actions"
import { AppointmentDrawerAdditionalInfo } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-additional-info"
import { AppointmentDrawerCardHeaderReviewAction } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-card-header-review-action"
import { AppointmentDrawerChecker } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-checker"
import { ErrorFallback } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-error-fallback"
import { AppointmentDrawerHeader } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-header"
import { RequestUploadFilesContainer } from "@/modules/waiting-room/components/request-upload-files-container"
import { useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"
import { SofiaEnvironment } from "@/sofia-environment"

const AppointmentDrawerPatientFragment = graphql`
  fragment appointmentDrawerPatient_WaitingRoomAppointment on WaitingRoomAppointment {
    appointment {
      patient {
        __typename
        id
        emailAddress
        phoneNumber
        ...PatientFragment
        ...appointmentDrawerHeader_patient
      }
    }
  }
`

const AppointmentDrawerDataFragment = graphql`
  fragment appointmentDrawerData_WaitingRoomAppointment on WaitingRoomAppointment {
    flexibility
    appointment {
      id
      channel
      chatDetails {
        chatThreadId
      }
      service
      reason
      status
      isCompleted
      questionnaireId
      telephoneLine {
        telephoneLineTranslations {
          language
          content
        }
      }
      appointmentAttachments {
        id
        url
        thumbnailUrl
        uploadDate
        attachmentType
        fileName
        uploadStatus
        uploadSource
      }
      patient {
        patientFileLock {
          lockedBy
        }
      }
      healthOverview {
        vitalSign {
          ...VitalSignFragment
        }
        generalHealthInformation {
          ...GeneralHealthInformationFragment
        }
      }
      ...RequestedMedicationsFragment
      fileUploadRequest {
        ...FileUploadRequestFragment
      }
      ...appointmentActions_appointment
      ...appointmentDrawerHeader_appointment
      ...appointmentDrawerCardHeader_appointment
      ...appointmentDrawerCardHeaderReviewAction_appointment
      ...appointmentDrawerCardHeaderReviewStatus_appointment
      ...appointmentDrawerQuestionnaire_appointment
      ...appointmentDrawerAdditionalInfo_appointment
      ...appointmentDrawerStatusIndicator_appointment
    }
  }
`

const AppointmentDrawerFragment = graphql`
  fragment appointmentDrawer_waitingRoomAppointment on WaitingRoomAppointment {
    ...appointmentDrawerPatient_WaitingRoomAppointment
    ...appointmentDrawerData_WaitingRoomAppointment
  }
`

type AppointmentDrawerContentProps = {
  onClose?: () => void
  isPreview: boolean
  waitingRoomAppointmentReference: appointmentDrawer_waitingRoomAppointment$key
}

const AppointmentDrawerContent = (props: AppointmentDrawerContentProps) => {
  const { waitingRoomAppointmentReference, isPreview, onClose } = props

  const appointmentDrawerFragmentReference = useFragment<appointmentDrawer_waitingRoomAppointment$key>(
    AppointmentDrawerFragment,
    waitingRoomAppointmentReference,
  )

  const { t } = useTranslation()
  const currentLanguage = useCurrentLanguage()
  const user = useUser()

  const { appointment: appointmentData, flexibility } = useFragment<appointmentDrawerData_WaitingRoomAppointment$key>(
    AppointmentDrawerDataFragment,
    appointmentDrawerFragmentReference,
  )

  const appointmentPatientData = useFragment<appointmentDrawerPatient_WaitingRoomAppointment$key>(
    AppointmentDrawerPatientFragment,
    appointmentDrawerFragmentReference,
  )

  const {
    appointment: { patient: appointmentDrawerPatientData },
  } = appointmentPatientData

  const { __typename, id: patientId } = appointmentDrawerPatientData
  const {
    id,
    channel,
    service,
    questionnaireId,
    reason,
    status,
    isCompleted,
    patient,
    appointmentAttachments,
    fileUploadRequest,
    telephoneLine,
    chatDetails,
  } = appointmentData

  const chatThreadId = chatDetails?.chatThreadId

  const country = __typename === "ch_Patient" ? "CH" : "DE"

  const { lockedBy } = patient.patientFileLock
  const patientData = useFragment<PatientFragment$key>(PatientFragment, appointmentDrawerPatientData)

  usePatientAttachmentSubscription(appointmentData.id)

  const attachments = appointmentAttachments
    .filter((attachment) => attachment.uploadSource !== "DOC2_U")
    .map((attachment) => ({
      ...attachment,
      url: `${SofiaEnvironment.Bff.Url}/${attachment.url}`,
      thumbnailUrl: attachment.thumbnailUrl ? `${SofiaEnvironment.Bff.Url}/${attachment.thumbnailUrl}` : null,
    }))

  const navigate = useNavigate()
  const setRequestTakeOver = useChatRequestTakeOverStore((store) => store.setRequestTakeOver)

  const openChatWithThread = useCallback(
    () => navigate(getChatRoute(chatThreadId ?? undefined)),
    [chatThreadId, navigate],
  )

  const openRequestTakeOverChat = useCallback(() => {
    const patientName = `${patientData.firstName} ${patientData.lastName}`
    setRequestTakeOver({ patientId: patientData.id, patientName })
    navigate(getChatRoute())
  }, [patientData, navigate, setRequestTakeOver])

  const openMedicalRecordWithoutAppointmentContext = useCallback(() => {
    window.open(`/patients/${country}/${patientId}`, patientId)
  }, [country, patientId])

  const openMedicalRecordWithAppointmentContext = useCallback(() => {
    window.open(`/patients/${country}/${patientId}?appointmentId=${id}`, id)
  }, [patientId, id, country])

  const openMedicalRecord = useCallback(() => {
    if (isCompleted || !canUserTakeAppointmentWithService(user, service)) {
      openMedicalRecordWithoutAppointmentContext()
    } else {
      openMedicalRecordWithAppointmentContext()
    }
  }, [isCompleted, user, service, openMedicalRecordWithoutAppointmentContext, openMedicalRecordWithAppointmentContext])

  const [requestTakeOverPatient, isRequestTakeOverPatientInFlight] = useRequestTakeOverPatientMutation()

  const setWaitingRoomPreviewDialog = useAppointmentContext((state) => state.setWaitingRoomPreviewDialog)

  const mapTelephoneLineContent = telephoneLine?.telephoneLineTranslations.find(
    (translation) => currentLanguage.toUpperCase() === convertLanguageToISOCode(translation.language),
  )?.content

  const isRequestable = canUserTakeAppointmentWithService(user, service)
  const isTakable = isRequestable && status === "OPEN" && (lockedBy === null || lockedBy === user.userName)
  const isChatChannel = channel === "CHAT"
  const isTakingByMe = status === "IN_PROGRESS" && lockedBy === user.userName
  const openPatientFileAfterTakeOver = isChatChannel ? openRequestTakeOverChat : openMedicalRecord

  return (
    <>
      <AppointmentDrawerHeader
        isPreview={isPreview}
        appointmentRef={appointmentData}
        appointmentPatientData={appointmentPatientData.appointment.patient}
        onClose={onClose}
      />
      <DrawerBody>
        <div className="flex flex-col items-start justify-start gap-4">
          <div
            key={id}
            className="animate-fade-in-top border-border bg-surface text-on-surface-subdued grid w-full content-start gap-4 rounded-xl border p-4 text-sm">
            <div className="flex items-center justify-between">
              <AppointmentDrawerCardHeader appointmentRef={appointmentData} />
              <div className="relative left-2 flex items-center gap-2">
                {!isPreview && (
                  <Button
                    variant="circle"
                    className="relative h-10 w-10 cursor-pointer"
                    data-testid="preview-appointment-btn"
                    onClick={(event) => {
                      event.stopPropagation()
                      setWaitingRoomPreviewDialog(true)
                    }}>
                    <MagnifyingGlass className="text-on-surface-subdued disabled:text-on-surface-disabled" />
                  </Button>
                )}
                <AppointmentDrawerCardHeaderReviewAction appointmentRef={appointmentData} />
                <AppointmentDrawerCardHeaderReviewStatus appointmentRef={appointmentData} />
                <div data-testid="action-cell">
                  {!isCompleted && user.isOneOf(["MedicalSupervisor"]) && (
                    <AppointmentActions appointmentRef={appointmentData} flexibility={flexibility}>
                      <Button
                        data-testid="action-button"
                        variant="circle"
                        className="h-10 w-10 p-0 outline-hidden!"
                        onClick={(event) => event.stopPropagation()}>
                        <DotsThreeVertical />
                      </Button>
                    </AppointmentActions>
                  )}
                </div>
              </div>
            </div>
            <p data-testid="appointment-reason" className="break-word-legacy">
              {reason}
            </p>
            {appointmentData.healthOverview?.vitalSign && (
              <VitalSignsSummary vitalSignKey={appointmentData.healthOverview.vitalSign} />
            )}
            {appointmentData.healthOverview?.generalHealthInformation && (
              <GeneralHealthInformationSummary
                generalHealthInformationKey={appointmentData.healthOverview.generalHealthInformation}
              />
            )}
            <RequestedMedicationsSummary appointmentRef={appointmentData} />
            {questionnaireId && <AppointmentDrawerQuestionnaire appointmentRef={appointmentData} />}
            {attachments.length !== 0 && <AttachmentList mediaFiles={attachments} appointmentId={appointmentData.id} />}
            {telephoneLine && mapTelephoneLineContent && (
              <CallLineBanner bannerInfo={mapTelephoneLineContent} testId="appointment-drawer-call-line-banner" />
            )}
            {!["CLOSED", "CANCELLED"].includes(status) && (
              <Suspense fallback={null}>
                <RequestUploadFilesContainer
                  appointmentId={id}
                  contactChannel={patientData.contactChannel}
                  isContactChannelVerified={patientData.isContactChannelVerified}
                  fileUploadRequestRef={fileUploadRequest}
                />
              </Suspense>
            )}
            <div className="flex flex-wrap gap-4 pt-4">
              <AppointmentDrawerStatusIndicator appointmentRef={appointmentData} />

              {isTakable && (
                <Button
                  className="px-4 py-2"
                  data-testid="take-consultation-btn"
                  data-faro-user-action-name="waiting_room_consultation_take"
                  isLoading={isRequestTakeOverPatientInFlight}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("waiting_room_consultation_take")

                    requestTakeOverPatient(
                      { patientId, appointmentId: id },
                      (response) => {
                        const chatThreadId =
                          response.requestTakeOverPatientFileLock.patientFileLockOutput?.inContextOfAppointment
                            ?.chatDetails?.chatThreadId

                        if (!isChatChannel) {
                          openMedicalRecordWithAppointmentContext()
                        } else if (chatThreadId) {
                          navigate(getTakenChatRoute(chatThreadId))
                        }
                      },
                      openPatientFileAfterTakeOver,
                    )
                  }}>
                  {t("scheduling.waitingRoom.takeConsultation")}
                </Button>
              )}

              {isTakingByMe && (
                <Button
                  variant="secondary"
                  className="px-4 py-2"
                  data-testid="open-consultation-btn"
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("waiting_room_consultation_open")
                    if (isChatChannel) {
                      openChatWithThread()
                    } else {
                      openMedicalRecord()
                    }
                  }}>
                  {t("scheduling.waitingRoom.goToConsultation")}
                </Button>
              )}

              {isRequestable && !isCompleted && lockedBy && lockedBy !== user.userName && (
                <Button
                  className="px-4 py-2"
                  data-testid="take-over-btn"
                  variant="secondary"
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("waiting_room_consultation_take_over")
                    requestTakeOverPatient(
                      { patientId, appointmentId: id },
                      openPatientFileAfterTakeOver,
                      openPatientFileAfterTakeOver,
                    )
                  }}>
                  {t("patient.takeOver.requestTakeover")}
                </Button>
              )}
            </div>
          </div>

          {isPreview && <AppointmentDrawerAdditionalInfo appointmentRef={appointmentData} />}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          data-testid="open-medical-record-btn"
          className="ml-auto"
          variant="tertiary"
          onClick={openMedicalRecordWithoutAppointmentContext}>
          {t("scheduling.waitingRoom.goToMedicalRecord")}
        </Button>
      </DrawerFooter>
    </>
  )
}

interface AppointmentDrawerProps extends React.ComponentProps<"div"> {
  tableRef: RefObject<HTMLDivElement | null>
  isPreview?: boolean
}

const AppointmentDrawer = (props: AppointmentDrawerProps) => {
  const { tableRef, isPreview, className, ...drawerProps } = props
  const appointmentRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const selectedWaitingRoomAppointmentId = useAppointmentContext((state) => state.selectedWaitingRoomAppointmentId)
  const hasAppointment = !!selectedWaitingRoomAppointmentId

  const setSelectedWaitingRoomAppointmentId = useAppointmentContext(
    (state) => state.setSelectedWaitingRoomAppointmentId,
  )

  useClickOutside((event: MouseEvent) => {
    if (tableRef.current && !tableRef.current.contains(event.target as HTMLElement) && !isPreview) {
      closeDrawer()
    }
  }, drawerRef)

  const closeDrawer = () => setSelectedWaitingRoomAppointmentId(null)

  const handleOpenDrawer = useCallback(() => {
    appointmentRef.current!.classList.add("w-125")
    appointmentRef.current!.classList.remove("hidden", "w-0")
  }, [])

  useEffect(() => {
    // By default, Appointment drawer will select previous appointment automatically.
    // This code snippet handles in case no previous appointment selected (root cause issue)
    if (!hasAppointment) {
      appointmentRef.current!.classList.remove("w-125")
      appointmentRef.current!.classList.add("hidden", "w-0")
    }
  }, [hasAppointment])

  return (
    <Drawer open>
      <div
        {...drawerProps}
        ref={appointmentRef}
        className={cn("absolute top-0 right-0 h-full duration-300! 2xl:relative", className, "hidden w-0")}>
        {hasAppointment && (
          <DrawerContent ref={drawerRef} onOpenAutoFocus={(e) => e.preventDefault()}>
            <ErrorBoundary fallback={<ErrorFallback isPreview={false} onClose={closeDrawer} />}>
              <Suspense>
                <AppointmentDrawerChecker
                  onClose={closeDrawer}
                  onOpenDrawer={handleOpenDrawer}
                  selectedId={selectedWaitingRoomAppointmentId}
                  isPreview={false}
                />
              </Suspense>
            </ErrorBoundary>
          </DrawerContent>
        )}
      </div>
    </Drawer>
  )
}

export {
  AppointmentDrawer,
  AppointmentDrawerContent,
  AppointmentDrawerFragment,
  AppointmentDrawerPatientFragment,
  AppointmentDrawerDataFragment,
}
